import { useEffect, useMemo, useState } from "react";
import { api, RestockProduct, RestockResponse, RestockStatus } from "../api";
import { formatNumber } from "../utils";
import { searchItems } from "../search";
import { GlassPanel, KpiCard, SkeletonTable, SkeletonKpi, ErrorState, DataTable, Badge, EmptyState } from "../components";
import type { Column } from "../components";

const STATUS_COLORS: Record<RestockStatus, string> = {
  urgent: "#ef4444",
  soon: "#f59e0b",
  ok: "#22c55e",
  idle: "#6b7280",
};

const STATUS_LABELS: Record<RestockStatus, string> = {
  urgent: "Cần đặt ngay",
  soon: "Sắp cần đặt",
  ok: "Đủ hàng",
  idle: "Không bán",
};

type FilterStatus = "all" | RestockStatus;

interface ConfigModal {
  product: RestockProduct;
  lead_time_days: number;
  safety_stock_days: number;
  notes: string;
  saving: boolean;
}

export default function PredictiveRestockPage() {
  const [data, setData] = useState<RestockResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<ConfigModal | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [dataDays, setDataDays] = useState<number | null>(null);

  const load = (d: number) => {
    setLoading(true);
    setError(null);
    api.inventoryRestock(d)
      .then((res) => setData(res))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(days); }, [days]);

  // Check data maturity
  useEffect(() => {
    fetch(`${api.baseUrl}/api/data-maturity`, { headers: api.authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((d: any) => setDataDays(d.days_with_receipts ?? 999))
      .catch(() => setDataDays(999));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.products;
    if (filter !== "all") rows = rows.filter((r) => r.status === filter);
    if (search.trim()) {
      rows = searchItems(rows, search, (r) => ({
        id: r.product_id,
        sku: r.sku,
        name: r.name,
      })).map(({ item }) => item);
    }
    return rows;
  }, [data, filter, search]);

  const openConfig = (product: RestockProduct) => {
    setModal({
      product,
      lead_time_days: product.lead_time_days,
      safety_stock_days: product.safety_stock_days,
      notes: "",
      saving: false,
    });
  };

  const saveConfig = async () => {
    if (!modal) return;
    setModal({ ...modal, saving: true });
    try {
      await api.restockConfig.update(modal.product.product_id, {
        lead_time_days: modal.lead_time_days,
        safety_stock_days: modal.safety_stock_days,
        notes: modal.notes || undefined,
      });
      setToast("Đã lưu cấu hình");
      setModal(null);
      load(days);
    } catch (e: any) {
      setToast("Lỗi: " + e.message);
      setModal({ ...modal, saving: false });
    }
  };

  const resetConfig = async () => {
    if (!modal) return;
    setModal({ ...modal, saving: true });
    try {
      await api.restockConfig.reset(modal.product.product_id);
      setToast("Đã reset về mặc định");
      setModal(null);
      load(days);
    } catch (e: any) {
      setToast("Lỗi: " + e.message);
      setModal({ ...modal, saving: false });
    }
  };

  const statusBadge = (status: RestockStatus) => {
    switch (status) {
      case "urgent": return <Badge variant="bad">{STATUS_LABELS.urgent}</Badge>;
      case "soon": return <Badge variant="warn">{STATUS_LABELS.soon}</Badge>;
      case "ok": return <Badge variant="ok">{STATUS_LABELS.ok}</Badge>;
      case "idle": return <Badge variant="info">{STATUS_LABELS.idle}</Badge>;
    }
  };

  const columns: Column<RestockProduct>[] = useMemo(() => [
    {
      key: "sku",
      header: "SKU",
      sortable: true,
      render: (r) => <span className="meta">{r.sku || "—"}</span>,
    },
    {
      key: "name",
      header: "Tên",
      sortable: true,
      render: (r) => <div style={{ fontWeight: 600 }}>{r.name}</div>,
    },
    {
      key: "current_stock",
      header: "Tồn",
      sortable: true,
      align: "right",
      render: (r) => formatNumber(r.current_stock),
    },
    {
      key: "daily_velocity",
      header: "Velocity/ngày",
      sortable: true,
      align: "right",
      render: (r) => r.daily_velocity > 0 ? r.daily_velocity.toFixed(2) : <span className="meta">0</span>,
    },
    {
      key: "lead_time_days",
      header: "Lead time",
      align: "right",
      render: (r) => `${r.lead_time_days}d`,
    },
    {
      key: "safety_stock_days",
      header: "Safety",
      align: "right",
      render: (r) => `${r.safety_stock_days}d`,
    },
    {
      key: "reorder_point",
      header: "ROP",
      sortable: true,
      align: "right",
      render: (r) => <span style={{ fontWeight: 600 }}>{formatNumber(r.reorder_point)}</span>,
    },
    {
      key: "days_until_reorder",
      header: "Còn N ngày",
      sortable: true,
      align: "right",
      render: (r) => {
        if (r.days_until_reorder === null) return <span className="meta">—</span>;
        const color = r.days_until_reorder <= 0 ? STATUS_COLORS.urgent : r.days_until_reorder <= 7 ? STATUS_COLORS.soon : STATUS_COLORS.ok;
        return <span style={{ color, fontWeight: 600 }}>{r.days_until_reorder}</span>;
      },
    },
    {
      key: "recommended_qty",
      header: "Đề xuất đặt",
      sortable: true,
      align: "right",
      render: (r) => r.recommended_qty > 0
        ? <span style={{ color: STATUS_COLORS.urgent, fontWeight: 600 }}>{formatNumber(r.recommended_qty)}</span>
        : <span className="meta">0</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      render: (r) => statusBadge(r.status),
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <button
          className="btn btn-sm"
          style={{ padding: "4px 8px", minWidth: 0 }}
          onClick={() => openConfig(r)}
          title="Cấu hình"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>settings</span>
        </button>
      ),
    },
  ], []);

  if (error && !data) {
    return <ErrorState error={error} onRetry={() => load(days)} />;
  }

  if (dataDays !== null && dataDays < 30) {
    return (
      <EmptyState
        state="not-enough-data"
        icon="psychology"
        title="Cần 30 ngày bán hàng để dự đoán"
        description="Hệ thống cần ít nhất 30 ngày dữ liệu đơn hàng để tính toán dự đoán đặt hàng chính xác."
        actualDays={dataDays}
        requiredDays={30}
        cta={{ label: "Xem đơn hàng", href: "/orders" }}
      />
    );
  }

  return (
    <>
      {/* Period selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <label className="meta">Khoảng thời gian:</label>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input"
          style={{ minWidth: 120 }}
        >
          <option value={30}>30 ngày</option>
          <option value={60}>60 ngày</option>
          <option value={90}>90 ngày</option>
        </select>
      </div>

      {/* KPI Cards */}
      {loading && !data ? (
        <div className="kpi-grid">
          <SkeletonKpi /><SkeletonKpi /><SkeletonKpi /><SkeletonKpi />
        </div>
      ) : data && (
        <div className="kpi-grid">
          <KpiCard
            label="Cần đặt ngay"
            value={`${data.summary.urgent}`}
            note="Tồn <= ROP"
            dotColor="bad"
          />
          <KpiCard
            label="Sắp cần đặt"
            value={`${data.summary.soon}`}
            note="Tồn <= 1.5x ROP"
            dotColor="warn"
          />
          <KpiCard
            label="Đủ hàng"
            value={`${data.summary.ok}`}
            note="Tồn > 1.5x ROP"
            dotColor="ok"
          />
          <KpiCard
            label="Không bán"
            value={`${data.summary.idle}`}
            note="Velocity = 0"
            dotColor=""
          />
        </div>
      )}

      {/* Table */}
      <GlassPanel
        title="Dự đoán đặt hàng"
        badge={`${filtered.length} sản phẩm`}
        badgeVariant="info"
      >
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterStatus)}
            className="input"
            style={{ minWidth: 140 }}
          >
            <option value="all">Tất cả</option>
            <option value="urgent">Cần đặt ngay</option>
            <option value="soon">Sắp cần đặt</option>
            <option value="ok">Đủ hàng</option>
            <option value="idle">Không bán</option>
          </select>
          <input
            type="text"
            className="input"
            placeholder="Tìm theo tên / SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
        </div>

        {loading && !data ? (
          <SkeletonTable rows={8} cols={10} />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>
            {data && data.products.length === 0
              ? "Chưa có dữ liệu tồn kho."
              : "Không tìm thấy sản phẩm phù hợp."}
          </div>
        ) : (
          <div className="table-wrap">
            <DataTable columns={columns} rows={filtered} rowKey={(r) => r.product_id} pageSize={50} />
          </div>
        )}
      </GlassPanel>

      {/* Config Modal */}
      {modal && (
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>Cấu hình: {modal.product.name}</h3>
              <button className="btn btn-sm" onClick={() => setModal(null)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="meta">Lead time (ngày giao hàng NCC)</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  max={90}
                  value={modal.lead_time_days}
                  onChange={(e) => setModal({ ...modal, lead_time_days: parseInt(e.target.value) || 7 })}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
              <div>
                <label className="meta">Safety stock (ngày tồn an toàn)</label>
                <input
                  type="number"
                  className="input"
                  min={0}
                  max={60}
                  value={modal.safety_stock_days}
                  onChange={(e) => setModal({ ...modal, safety_stock_days: parseInt(e.target.value) || 3 })}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
              <div>
                <label className="meta">Ghi chú</label>
                <input
                  type="text"
                  className="input"
                  placeholder="VD: NCC giao chậm, cần buffer thêm..."
                  value={modal.notes}
                  onChange={(e) => setModal({ ...modal, notes: e.target.value })}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={resetConfig} disabled={modal.saving}>
                Reset mặc định
              </button>
              <button className="btn btn-primary" onClick={saveConfig} disabled={modal.saving}>
                {modal.saving ? "Đang lưu..." : "Lưu"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, background: "#1e293b", color: "#fff",
          padding: "12px 20px", borderRadius: 8, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}>
          {toast}
        </div>
      )}
    </>
  );
}
